import { Child } from 'pym.js';
import * as d3 from 'd3';
import meta from './data/meta.json';

// import Zion from './utils/Zion';

// polyfills
import './utils/polyfills'; // (includes Array.findIndex && Array.find)

// utils
import { stylePremium } from './utils/helpers';

// styles
import './styles/graphic.scss';

let latestData;

const mapboxgl = require('mapbox-gl');

const pkg = require('../package.json');

const ai2html = null;

if (process.env.NODE_ENV === 'development') {
  // turning ai2html's output into a module allows
  // webpack to apply hot module replacement.
  // we obviously want to bake it into the html in production
  // that's why we're conditionally requiring it
  // ai2html = require('./ai2html-graphic.html');
}

// eslint-disable-next-line  no-use-before-define
window.pymChild = new Child({ renderCallback: draw, id: pkg.name });

console.clear();

window.onload = () => {
  // necessary evil for right now
  if (process.env.NODE_ENV === 'development') {
    Object.keys(meta).forEach((id) => {
      const node = document.querySelector(`.${id}`);

      if (node) {
        node.innerHTML = meta[id];
      }
    });
    drawMap();
  }

  const { pymChild } = window;
  pymChild.sendHeight();

  // If embedded, send Zion page view event
  if (window.pymChild.parentUrl) {
    // const zion = new Zion(pkg.name, pymChild.parentUrl.split('?')[0]);
  }
};

const CNNPolitics = false;
const CNNBusiness = false;

stylePremium(CNNPolitics, CNNBusiness);

let chartData;
let rawData;
let lastUpdated;
let subheadText;

function draw() {
  if (process.env.NODE_ENV === 'development') {
  }
}

mapboxgl.accessToken = 'pk.eyJ1IjoiY25uZGlnaXRhbCIsImEiOiJiYjIwMTgxNDZmNjgyODllMjAxYTYwOTAxYmY2MzI2MyJ9.8F6TQITXymFzCXy8tBqGgg';

const FIRE_DATA_URL = 'https://ix.cnn.io/data/europe-wildfires/J1_VIIRS_C2_Europe_7d.geojson';
const BURNED_AREA_URL = 'https://ix.cnn.io/data/europe-wildfires/burned_area_last30.geojson';

const MAP_ID = 'mapbox://styles/cnndigital/cl960ytee001215qu1uhorjlx';
const TILESET_ID = 'cnndigital.icgwlq9rd369';
const LAYER_ID = '2a1493fe3a9e86b0375d';
const LAYER_OPACITY = 1;

let mapParams;
let hasLocation;
const usingParentDotHtml = false;
let outageEvent;

const mapBounds = [
  [-82.85674461952952, 30.67805992908662], // Southwest corner: [longitude, latitude]
  [-81.5954552376801, 31.45487772268902], // Northeast corner: [longitude, latitude]
];

const config = {
  europe: {
    hed: 'Tracking the wildfires in Europe',
    dataset: 'fires',
    desktop: {
      initZoom: 3.25,
      minZoom: 3.25,
      maxZoom: 12,
      center: [8.025434917584477, 48.96046938112325],
    },
    mobile: {
      initZoom: 2.25,
      minZoom: 2.25,
      maxZoom: 11,
      center: [8.025434917584477, 48.96046938112325],
    },
  },
  gironde: {
    hed: 'Tracking the wildfires in and around France’s Gironde region',
    dataset: 'burnedArea',
    desktop: {
      initZoom: 8.75,
      minZoom: 8.5,
      maxZoom: 12,
      center: [-0.9799852368239499, 44.65],
    },
    mobile: {
      initZoom: 7.25,
      minZoom: 7.25,
      maxZoom: 12,
      center: [-0.9799852368239499, 44.65],
    },
  },
  madrid: {
    hed: 'Tracking the wildfires near Madrid',
    dataset: 'burnedArea',
    desktop: {
      initZoom: 7.5,
      minZoom: 7.5,
      maxZoom: 12,
      center: [-4.223148152306663, 40.421133209392906],
    },
    mobile: {
      initZoom: 7.45,
      minZoom: 7.45,
      maxZoom: 11,
      center: [-4.223148152306663, 40.421133209392906],
    },
  },
  spainfrance: {
    hed: 'Tracking the wildfires in Spain and France',
    dataset: 'fires',
    desktop: {
      initZoom: 4.390551403589817,
      minZoom: 4.390551403589817,
      maxZoom: 12,
      center: [-2.3155232398272574, 42.798604476373725],
    },
    mobile: {
      initZoom: 3.8,
      minZoom: 3.8,
      maxZoom: 12,
      center: [-2.3155232398272574, 42.798604476373725],
    },
  },
};


// Get excerpt ID from URL parameter
const getMapId = () => {
  const params = new URLSearchParams(window.location.search);
  return params.get('id') || 'europe';
};

const drawMap = () => {
  let mapId = getMapId();
  if (Object.keys(config).indexOf(mapId) < 0) {
    mapId = 'europe';
  }
  const optionsSm = config[mapId].mobile;
  const optionsLg = config[mapId].desktop;

  const headline = config[mapId].hed;
  const headlineEl = d3.select('#meta .headline');
  headlineEl.html(headline);

  const dataset = config[mapId].dataset || 'fires';

  const legendEl = d3.select('.fire-legend');
  if (dataset === 'burnedArea') {
    legendEl.html(`
      <ul class="legend legend--burned-area">
        <li class="burned-area-key">Area burned in the last 30 days</li>
      </ul>
    `);
    d3.select('#footer .footnote').html('Data as of August 1, 2026 at 7:28 a.m. ET.');
    d3.select('#footer .source').html('Source: European Forest Fire Information System');
  } else {
    legendEl.html(`
      <p class="legend-header">Active fires detected by satellite:</p>
      <ul class="legend">
        <li class="level-1">Today</li>
        <li class="level-2">Yesterday</li>
        <li class="level-3">In the last week</li>
      </ul>
    `);
  }

  const container = d3.select('#map');
  container.html('');

  const mq = window.matchMedia('(min-width: 640px)');
  const isDesktop = window.matchMedia('(min-width: 640px)').matches;

  let options;

  const updateOptions = (opts, isSmall) => {
    if (mapParams && mapParams.lnglat) {
      opts.center = mapParams.lnglat;
      opts.initZoom = (isSmall) ? mapParams.z - 0.75 : mapParams.z;
    }
    return opts;
  };

  const handleBrowserSize = () => {
    if (mq.matches) {
      d3.select('#map').attr('style', 'width: 100%; height: 575px;');
      options = optionsLg;
      options = updateOptions(optionsLg, false);
    } else {
      d3.select('#map').attr('style', 'width: 100%; height: 300px;');
      options = optionsSm;
      options = updateOptions(optionsSm, true);
    }
  };
  handleBrowserSize();

  const map = new mapboxgl.Map({
    container: 'map',
    style: MAP_ID,
    center: options.center,
    zoom: options.initZoom,
    maxZoom: options.maxZoom,
    minZoom: options.minZoom,
    bearing: 0,
    scrollZoom: true,
    dragRotate: false,
    logoPosition: 'bottom-right',
    touchPitch: false,
    interactive: true,
    // projection: 'albers',
    maxBounds: options.mapBounds,
  });

  // disable map rotation using right click + drag
  map.dragRotate.disable();

  // disable map rotation using touch rotation gesture
  map.touchZoomRotate.disableRotation();

  const nav = new mapboxgl.NavigationControl({
    showCompass: false,
  });
  map.addControl(nav, 'top-left');

  map.on('load', () => {
    map.on('moveend', () => {
      console.log('zoom: ',
        map.getZoom(),
        ',\n',
        'center: [',
        map.getCenter().lng, ',',
        map.getCenter().lat, '],');
    });
    if (dataset === 'burnedArea') {
      map.addSource('burned-area', {
        type: 'geojson',
        data: BURNED_AREA_URL,
        generateId: true,
      });

      map.addLayer({
        id: 'burned-area-fill',
        type: 'fill',
        source: 'burned-area',
        paint: {
          'fill-color': '#c52622',
          'fill-opacity': 0.55,
        },
      }, 'settlement-subdivision-label');

      map.addLayer({
        id: 'burned-area-outline',
        type: 'line',
        source: 'burned-area',
        paint: {
          'line-color': '#c52622',
          'line-width': 0.75,
        },
      }, 'settlement-subdivision-label');
    } else {
      map.addSource('local-geojson', {
        type: 'geojson',
        data: FIRE_DATA_URL,
        generateId: true,
      });

      map.addLayer({
        id: 'fire-layer-past-week',
        type: 'circle',
        source: 'local-geojson',
        paint: {
          // 'circle-radius': 1,
          'circle-color': 'blue',
          'circle-radius': [
            'interpolate',
            ['exponential', 1.75],
            ['zoom'],
            2.5,
            mq.matches ? 2 : 1.5,
            12,
            mq.matches ? 3.25 : 3.5,
          ],
          'circle-color': 'rgba(241, 136, 81, 0.40)',
        },
        filter: [
          '<', ['get', 'day_index'], 6,
        ],
      }, 'settlement-subdivision-label');

      map.addLayer({
        id: 'fire-layer-past-2days',
        type: 'circle',
        source: 'local-geojson',
        paint: {
          // 'circle-radius': 1,
          'circle-color': 'blue',
          'circle-radius': [
            'interpolate',
            ['exponential', 1.75],
            ['zoom'],
            2.5,
            mq.matches ? 2 : 1.5,
            12,
            mq.matches ? 3.25 : 3.5,
          ],
          'circle-color': 'rgba(241, 136, 81, 0.90)',
        },
        filter: [
          '==', ['get', 'day_index'], 6,
        ],
      }, 'settlement-subdivision-label');

      // Add the text label layer
      map.addLayer({
        id: 'fire-layer-past-day',
        type: 'circle',
        source: 'local-geojson',
        paint: {
          // 'circle-radius': 1,
          'circle-color': 'blue',
          'circle-radius': [
            'interpolate',
            ['exponential', 1.75],
            ['zoom'],
            2.5,
            mq.matches ? 2 : 1.5,
            12,
            mq.matches ? 3.25 : 3.5,
          ],
          'circle-color': '#c52622',
        },
        filter: [
          '==', ['get', 'day_index'], 7,
        ],
      }, 'settlement-subdivision-label');
    }

    // map.addLayer({
    //   id: 'fire-layer',
    //   sourceLayer: LAYER_ID,
    //   'source-layer': LAYER_ID,
    //   type: 'circle',
    //   source: 'local-geojson',
    //   paint: {
    //     // 'circle-radius': 1,
    //     'circle-color': 'blue',
    //     'circle-radius': [
    //       'interpolate',
    //       ['exponential', 1.75],
    //       ['zoom'],
    //       2.5,
    //       mq.matches ? 8 : 1.5,
    //       12,
    //       mq.matches ? 3.25 : 3.5,
    //     ],
    //     'circle-color': [
    //       'match',
    //       ['get', 'day_index'],
    //       7,
    //       '#c52622',
    //       6,
    //       'rgba(241, 136, 81, 0.90)',
    //       /* other */ 'rgba(241, 136, 81, 0.40)',
    //     ],
    //   },
    // }, 'settlement-subdivision-label');


    // Set custom zoom ranges
    map.setLayerZoomRange('settlement-major-label', 4);
    map.setLayerZoomRange('settlement-subdivision-label', 4);
    map.setLayerZoomRange('settlement-minor-label', 4);
  });
};

drawMap();

let prevW = window.innerWidth;
const redraw = () => {
  if (!map || window.innerWidth === prevW) return;
  map.setMaxBounds(options.mapBounds);
  map.setMaxZoom(options.maxZoom);
  map.setMinZoom(options.minZoom);
  map.setZoom(options.initZoom);
  map.setCenter(options.center);
  map.resize();
  prevW = window.innerWidth;
};
window.addEventListener('resize', drawMap);

// fyi, disabling an empty mapbox button in graphic.scss with
// .mapboxgl-ctrl-attrib-button { display: none }
